import {
  getV1CompaniesCompanyIdPaymentTerms,
  patchV1PaymentTermsId,
  PaymentTerm_Create,
  PaymentTerm_Update,
  postV1CompaniesCompanyIdPaymentTerms,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { PAYMENT_TERMS_QUERY_KEY } from '~queries/constants';
import { queryClient } from '~queries/queryClient';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import fetchAllPages from '~utils/fetchAllPages';

import { PaymentTerm } from './models';

interface FetchPaymentTermsPageParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
  signal?: AbortSignal;
}

export default function usePaymentTerms() {
  const fetchPage = useCallback(
    async ({
      companyId,
      cursor,
      limit = DEFAULT_API_PAGE_SIZE,
      link,
      signal,
    }: FetchPaymentTermsPageParams) => {
      try {
        const response = await getV1CompaniesCompanyIdPaymentTerms({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });
        const data = response.data.data.map(PaymentTerm.parse);
        const pagination = extractPagination(response);

        return { data, pagination };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.payment_terms.failed_to_fetch'),
          );
        } catch {
          // Error already handled (toast + Sentry)
        }

        return undefined;
      }
    },
    [],
  );

  const fetchAll = useCallback(
    (companyId: string, signal?: AbortSignal) =>
      fetchAllPages(
        (cursor, limit, pageSignal) =>
          fetchPage({
            companyId,
            limit,
            signal: pageSignal,
            ...(cursor ? { cursor, link: 'after' as const } : {}),
          }),
        { signal },
      ),
    [fetchPage],
  );

  const create = useCallback(async (companyId: string, body: PaymentTerm_Create) => {
    try {
      const response = await postV1CompaniesCompanyIdPaymentTerms({
        path: { 'company-id': companyId },
        body,
      });

      await queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_QUERY_KEY] });

      return PaymentTerm.parse(response.data.data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.payment_terms.failed_to_save'),
      );
      throw error;
    }
  }, []);

  const update = useCallback(
    async (companyId: string, id: string, body: PaymentTerm_Update) => {
      try {
        const response = await patchV1PaymentTermsId({
          path: { 'company-id': companyId, id },
          body,
        });
        await queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_QUERY_KEY] });

        return PaymentTerm.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.payment_terms.failed_to_save'),
        );
        throw error;
      }
    },
    [],
  );

  return { create, fetchAll, fetchPage, update };
}
